import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Calendar,
  CheckCircle2,
  Clock,
  Download,
  Gauge as GaugeIcon,
  Globe,
  IndianRupee,
  Info,
  Lightbulb,
  ListChecks,
  Mail,
  Rocket,
  RotateCcw,
  ShieldAlert,
  Target,
  TrendingDown,
  TrendingUp,
  Zap
} from "lucide-react";
import Gauge, { scoreColor } from "./Gauge";
import { formatINR } from "./ReportMapper";

const EMEIS_EMAIL = "contactus@emeistechnologies.com";
const EMEIS_SITE = "https://www.emeistechnologies.com";

export const openStrategyCall = (company = {}, report = {}) => {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `AI Strategy Consultation: ${company.companyName || ""} × Emeis Technologies`,
    details: [
      "Discussion on AI Growth Report",
      "",
      `Company: ${company.companyName || "N/A"}`,
      `Industry: ${company.industryLabel || company.industry || "N/A"}`,
      `AI Score: ${report.score ?? "N/A"}`,
      `Estimated annual value: ${formatINR(report.roi)}`
    ].join("\n"),
    add: [company.email, EMEIS_EMAIL].filter(Boolean).join(","),
    location: "Google Meet"
  });
  window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, "_blank", "noopener");
};

const BREAKDOWN = [
  { key: "data_maturity", label: "Data maturity" },
  { key: "automation", label: "Process automation" },
  { key: "ai_readiness", label: "AI readiness" },
  { key: "digital_adoption", label: "Digital adoption" }
];

const POSITION_STYLE = {
  Leader: { bg: "#f0fdf4", fg: "#16a34a" },
  Competitive: { bg: "#fffbeb", fg: "#d97706" },
  Lagging: { bg: "#fef2f2", fg: "#dc2626" }
};

const PHASE_COLORS = ["#0b1220", "#f26b1d", "#0ea5a4"];

const Section = ({ title, icon: Icon, children, className = "" }) => (
  <section className={`card report-section ${className}`}>
    <h3 className="section-title">
      <span className="ic"><Icon size={17} /></span>
      {title}
    </h3>
    {children}
  </section>
);

// `admin` (optional) switches to the internal view: { onBack, date, top, bottom, actions }.
export default function ReportDashboard({ report, company = {}, onStartOver, admin }) {
  const bench = report.industry_benchmark;
  const position = POSITION_STYLE[bench.position] || POSITION_STYLE.Competitive;
  const industry = company.industryLabel || "your industry";
  const date = new Date(admin?.date || Date.now()).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  const emailBody = [
    "Hi Emeis team,",
    "",
    "I'd like to discuss my AI Growth Report.",
    "",
    `Company: ${company.companyName}`,
    `Industry: ${industry}`,
    `AI Score: ${report.score}/100`,
    `Estimated annual value: ${formatINR(report.roi)}`,
    "",
    `${company.name || ""}`,
    `${company.mobile || ""}`
  ].join("\n");
  const mailto = `mailto:${EMEIS_EMAIL}?subject=${encodeURIComponent("AI Growth Report: " + (company.companyName || ""))}&body=${encodeURIComponent(emailBody)}`;

  return (
    <div className="page">
      <div className="report-toolbar no-print">
        <div className="container-wide inner">
          {admin && (
            <button className="btn btn-ghost" onClick={admin.onBack} title="All scans">
              <ArrowLeft size={16} /> <span>All scans</span>
            </button>
          )}
          <img src="./emeis.png" alt="Emeis Technologies" className="logo" />
          <div className="spacer" />
          {admin?.actions}
          <button className="btn btn-ghost" onClick={() => window.print()} title="Download PDF">
            <Download size={16} /> <span>Download PDF</span>
          </button>
          {!admin && (
            <button className="btn btn-accent" onClick={() => openStrategyCall(company, report)} title="Book a call">
              <Calendar size={16} /> <span>Book strategy call</span>
            </button>
          )}
        </div>
      </div>

      <div className="report-top">
      <div className="container-wide">
        <div className="print-only" style={{ marginBottom: 16 }}>
          <img src="./emeis.png" alt="Emeis Technologies" style={{ height: 30 }} />
        </div>

        {/* HERO */}
        <div className="report-hero">
          <div style={{ position: "relative", zIndex: 1 }}>
            <span className="tag"><Zap size={13} /> AI Growth Report · {industry}</span>
            <h1>{company.companyName}</h1>
            <div className="meta">Prepared for {company.name} · {date}</div>
            {report.summary && <p className="summary">{report.summary}</p>}
          </div>
          <div className="gauge-panel">
            <Gauge
              value={report.score}
              size={160}
              stroke={14}
              label="AI maturity score"
              color="url(#scoreGradient)"
              track="rgba(255,255,255,0.1)"
              textColor="#fff"
            />
            <div><span className="level">{report.maturity_level}</span></div>
          </div>
        </div>
      </div>
      </div>

      <div className="container-wide report-body">
        {admin?.top}

        {report.is_fallback && (
          <div className="alert alert-info fallback-note">
            <Info size={18} />
            This is a preliminary assessment. Book a strategy call for a detailed, fully personalised analysis.
          </div>
        )}

        {/* KPIs */}
        <div className="kpi-grid">
          <div className="kpi">
            <div className="ic" style={{ background: "#f0fdf4", color: "#16a34a" }}><IndianRupee size={18} /></div>
            <div className="v">{formatINR(report.roi)}</div>
            <div className="l">Estimated annual value</div>
          </div>
          <div className="kpi">
            <div className="ic" style={{ background: "#fff4ec", color: "#d9570d" }}><TrendingDown size={18} /></div>
            <div className="v">{report.cost_reduction_percent}%</div>
            <div className="l">Potential cost reduction</div>
          </div>
          <div className="kpi">
            <div className="ic" style={{ background: "#ecfeff", color: "#0e7490" }}><TrendingUp size={18} /></div>
            <div className="v">{report.revenue_uplift_percent}%</div>
            <div className="l">Potential revenue uplift</div>
          </div>
          <div className="kpi">
            <div className="ic" style={{ background: "#f1f5f9", color: "#0b1220" }}><Clock size={18} /></div>
            <div className="v">{report.payback_months ? `${report.payback_months} mo` : "–"}</div>
            <div className="l">Estimated payback</div>
          </div>
        </div>

        <div className="two-col">
          {/* BREAKDOWN */}
          <Section title="Score breakdown" icon={BarChart3} className="">
            {BREAKDOWN.map(b => {
              const v = report.score_breakdown[b.key] || 0;
              return (
                <div className="bar-row" key={b.key}>
                  <div className="top"><span>{b.label}</span><b style={{ color: scoreColor(v) }}>{v}</b></div>
                  <div className="bar"><div style={{ width: `${v}%`, background: scoreColor(v) }} /></div>
                </div>
              );
            })}
          </Section>

          {/* BENCHMARK */}
          <Section title="Industry benchmark" icon={GaugeIcon}>
            <span className="position-badge" style={{ background: position.bg, color: position.fg }}>
              {bench.position}
            </span>
            <div className="bench-track">
              <div className="bench-marker" style={{ left: `${bench.industry_avg_score}%` }}>
                <div className="dot" style={{ background: "#94a3b8" }} />
                <div className="lbl down">Avg {bench.industry_avg_score}</div>
              </div>
              <div className="bench-marker" style={{ left: `${bench.top_quartile_score}%` }}>
                <div className="dot" style={{ background: "#16a34a" }} />
                <div className="lbl down">Top 25% {bench.top_quartile_score}</div>
              </div>
              <div className="bench-marker" style={{ left: `${report.score}%`, zIndex: 2 }}>
                <div className="dot" style={{ background: "#f26b1d", width: 22, height: 22 }} />
                <div className="lbl up" style={{ color: "#d9570d" }}>You {report.score}</div>
              </div>
            </div>
            <p style={{ margin: 0, color: "var(--ink-2)", fontSize: 14 }}>
              {bench.commentary || `Your AI maturity compared with other ${industry} businesses of a similar size.`}
            </p>
          </Section>
        </div>

        {/* OPPORTUNITIES */}
        {report.opportunities.length > 0 && (
          <Section title={`Top AI opportunities for ${industry}`} icon={Lightbulb}>
            <div className="opp-grid">
              {report.opportunities.map(o => (
                <div className="opp" key={o.id}>
                  <div className="head">
                    <span className="idx">{o.id}</span>
                    <h4>{o.title}</h4>
                  </div>
                  {o.description && <p>{o.description}</p>}
                  <div className="badges">
                    {o.impact && <span className={`badge ${o.impact.toLowerCase()}`}>{o.impact} impact</span>}
                    {o.effort && <span className={`badge effort-${o.effort.toLowerCase()}`}>{o.effort} effort</span>}
                    {o.category && <span className="badge">{o.category}</span>}
                    {o.estimated_annual_value > 0 && (
                      <span className="badge value">~{formatINR(o.estimated_annual_value)}/yr</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* QUICK WINS + KPIs */}
        {(report.quick_wins.length > 0 || report.kpis.length > 0) && (
          <div className="two-col">
            {report.quick_wins.length > 0 && (
              <Section title="Quick wins (next 30 days)" icon={Rocket}>
                <ul className="check-list">
                  {report.quick_wins.map((w, i) => <li key={i}><CheckCircle2 size={17} />{w}</li>)}
                </ul>
              </Section>
            )}
            {report.kpis.length > 0 && (
              <Section title="KPIs to track" icon={Target}>
                <ul className="check-list kpis">
                  {report.kpis.map((k, i) => <li key={i}><ListChecks size={17} />{k}</li>)}
                </ul>
              </Section>
            )}
          </div>
        )}

        {/* ROADMAP */}
        {report.roadmap.length > 0 && (
          <Section title="Your AI transformation roadmap" icon={Calendar}>
            <div className="timeline">
              {report.roadmap.map((p, i) => (
                <div className="phase" key={p.id} style={{ borderTop: `4px solid ${PHASE_COLORS[i % 3]}` }}>
                  <div className="ph-top">
                    <span className="ph-num" style={{ background: PHASE_COLORS[i % 3] }}>{p.phase}</span>
                    {p.timeframe && <span className="ph-time"><Clock size={13} />{p.timeframe}</span>}
                  </div>
                  {p.title && <h4>{p.title}</h4>}
                  <ul>{p.actions.map((a, j) => <li key={j}>{a}</li>)}</ul>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* RISKS */}
        {report.risks.length > 0 && (
          <Section title="Risks & mitigations" icon={ShieldAlert}>
            {report.risks.map((r, i) => (
              <div className="risk" key={i}>
                <div className="r"><AlertTriangle size={16} />{r.risk}</div>
                <div className="m">{r.mitigation}</div>
              </div>
            ))}
          </Section>
        )}

        {/* CHALLENGES */}
        {company.challenges && (
          <Section title={admin ? "Challenges they shared" : "Challenges you shared"} icon={Target}>
            <p style={{ margin: 0, color: "var(--ink-2)", whiteSpace: "pre-line" }}>{company.challenges}</p>
          </Section>
        )}

        {admin?.bottom}

        {/* CTA */}
        {!admin && <div className="cta-card">
          <div style={{ flex: "1 1 320px" }}>
            <h2>Turn this report into results</h2>
            <p>Talk to Emeis AI experts about implementing these opportunities for {company.companyName}.</p>
            <div className="links">
              <a href={`mailto:${EMEIS_EMAIL}`}><Mail size={15} />{EMEIS_EMAIL}</a>
              <a href={EMEIS_SITE} target="_blank" rel="noreferrer"><Globe size={15} />www.emeistechnologies.com</a>
            </div>
          </div>
          <div className="no-print" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-accent btn-lg" onClick={() => openStrategyCall(company, report)}>
              <Calendar size={18} /> Book free strategy call
            </button>
            <a className="btn btn-ghost btn-lg" href={mailto}><Mail size={18} /> Email us</a>
          </div>
        </div>}

        <div className="report-footer">
          {!admin && <div className="no-print" style={{ marginBottom: 12 }}>
            <button className="btn btn-ghost" onClick={onStartOver}>
              <RotateCcw size={16} /> Start a new scan
            </button>
          </div>}
          © Emeis Technologies · AI Growth Scanner · Confidential report for {company.companyName}
        </div>
      </div>
    </div>
  );
}
